import { ReactNode, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sidebar } from "./Sidebar";
import { PageTransition } from "@/components/ui/PageTransition";
import { cn } from "@/lib/utils";
import { Footer } from "@/components/footer";

interface MainLayoutProps {
  children: ReactNode;
  className?: string;
  requireAuth?: boolean;
}

export function MainLayout({ 
  children, 
  className,
  requireAuth = true
}: MainLayoutProps) {
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Show loading state
  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If it's the login page, show a simplified layout without sidebar
  if (!requireAuth) {
    return (
      <div className="min-h-screen flex w-full bg-background">
        <div className="flex-1 flex flex-col min-h-screen">
          <main className={cn("flex-1 container py-6", className)}>
            <PageTransition>
              {children}
            </PageTransition>
          </main>
          <Footer />
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (requireAuth && !user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen flex w-full bg-background">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      
      <div className={cn(
        "flex-1 flex flex-col min-h-screen transition-all duration-300",
        sidebarOpen ? "lg:pl-64" : "lg:pl-0"
      )}>
        <main className={cn("flex-1 container py-6", className)}>
          <PageTransition>
            {children}
          </PageTransition>
        </main>
        <Footer />
      </div>
    </div>
  );
}
